import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface ReviewerLoadRow {
  reviewer_id: string;
  reviewer_name: string;
  review_count: number;
}

export interface ThroughputPoint {
  /** ISO date string, e.g. "2026-04-07" */
  date: string;
  completed: number;
}

export interface FindingTypeRow {
  rule_key: string;
  label: string;
  count: number;
}

export interface TeamMonitoringData {
  avg_review_time_minutes: number | null;
  override_rate_pct: number | null;
  escalation_rate_pct: number | null;
  reviewer_load: ReviewerLoadRow[];
  throughput_last_30d: ThroughputPoint[];
  top_finding_types: FindingTypeRow[];
}

// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * Fetches team monitoring metrics from GET /api/v1/dashboard/team-monitoring.
 * Admin-only endpoint — returns 403 for non-admin callers (query is disabled for
 * non-admins at the component level; this hook is only mounted inside
 * TeamMonitoringTab which is admin-gated).
 *
 * FR-083: Team performance dashboard (contracts per reviewer, avg review time).
 * FR-084: Most frequent finding types report.
 */
export function useTeamMonitoring() {
  return useQuery<TeamMonitoringData>({
    queryKey: ['dashboard', 'team-monitoring'],
    queryFn: () => api.get<TeamMonitoringData>('/api/v1/dashboard/team-monitoring'),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    staleTime: 30_000,
  });
}
